import React, { useCallback } from "react";
import * as XLSX from "xlsx";

import MainPageHOC from "../../layout/hoc/MainPageHOC";
import MainPageTableHeader from "../../layout/body/MainPageTableHeader";

import PaginationComponent from "../../components/pagination/PaginationComponent";
import FetcherComponent from "../../components/shared/FetcherComponent";
import MainPageHeader from "../../components/shared/MainPageHeader";
import OlympiadPageFilters from "../../components/olympiadPage/OlympiadPageFilters";

import { useOlympiadPageContext } from "../../hooks/useOlympiadPageContext";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import OlympiadPageTableBody from "../../components/olympiadPage/OlympiadPageTableBody";
import { olympiadService } from "../../services/olympiadService";
import {
  OLYMPIAD_FILTER_PARAM_KEYS,
  OLYMPIAD_TYPE_OPTIONS,
} from "../../utilities/constants";

const readFiltersFromUrl = () => {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const df = p.get(OLYMPIAD_FILTER_PARAM_KEYS.DATE_FROM) || "";
  const dt = p.get(OLYMPIAD_FILTER_PARAM_KEYS.DATE_TO) || "";
  const region = p.get(OLYMPIAD_FILTER_PARAM_KEYS.REGION) || "";
  const ot = p.get(OLYMPIAD_FILTER_PARAM_KEYS.OLYMPIAD_TYPE);
  const search = p.get(OLYMPIAD_FILTER_PARAM_KEYS.SEARCH) || "";
  return {
    createdDateFrom: df,
    createdDateTo: dt,
    region: region,
    olympiadType: ot
      ? OLYMPIAD_TYPE_OPTIONS.find((o) => o.value === ot) || {
          value: ot,
          label: ot,
        }
      : null,
    search: search || "",
  };
};

const OlympiadPage = () => {
  const {
    applicants,
    setApplicants,
    allApplicants,
    setAllApplicants,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalRecords,
    setTotalRecords,
    setPageSize,
    pageSize,
  } = useOlympiadPageContext();

  const [createdDateFrom, setCreatedDateFrom] = React.useState(
    () => readFiltersFromUrl().createdDateFrom
  );
  const [createdDateTo, setCreatedDateTo] = React.useState(
    () => readFiltersFromUrl().createdDateTo
  );
  const [region, setRegion] = React.useState(() => readFiltersFromUrl().region);
  const [olympiadType, setOlympiadType] = React.useState(
    () => readFiltersFromUrl().olympiadType
  );

  const {
    search,
    setSearch,
    searchQuery,
    updateFilterParams,
    clearFilterParams,
    searchParams,
  } = useUrlFilters({
    filterParamKeys: OLYMPIAD_FILTER_PARAM_KEYS,
    readFiltersFromUrl,
    onUrlChange: useCallback((data) => {
      setCreatedDateFrom(data.createdDateFrom);
      setCreatedDateTo(data.createdDateTo);
      setRegion(data.region);
      setOlympiadType(data.olympiadType);
    }, []),
  });

  const currentPageQueryString = searchParams.get("current-page");
  const pageSizeQueryString = searchParams.get("page-size");

  const handleCreatedDateFromChange = useCallback(
    (v) => {
      setCreatedDateFrom(v);
      updateFilterParams({
        [OLYMPIAD_FILTER_PARAM_KEYS.DATE_FROM]: v || null,
      });
    },
    [updateFilterParams]
  );

  const handleCreatedDateToChange = useCallback(
    (v) => {
      setCreatedDateTo(v);
      updateFilterParams({
        [OLYMPIAD_FILTER_PARAM_KEYS.DATE_TO]: v || null,
      });
    },
    [updateFilterParams]
  );

  const handleRegionChange = useCallback(
    (v) => {
      setRegion(v);
      updateFilterParams({
        [OLYMPIAD_FILTER_PARAM_KEYS.REGION]: v || null,
      });
    },
    [updateFilterParams]
  );

  const handleOlympiadTypeChange = useCallback(
    (selected) => {
      setOlympiadType(selected);
      updateFilterParams({
        [OLYMPIAD_FILTER_PARAM_KEYS.OLYMPIAD_TYPE]: selected?.value || null,
      });
    },
    [updateFilterParams]
  );

  const handleClearAllFilters = useCallback(() => {
    setCreatedDateFrom("");
    setCreatedDateTo("");
    setRegion("");
    setOlympiadType(null);
    setSearch("");
    clearFilterParams();
  }, [clearFilterParams, setSearch]);

  const fetchUsers = useCallback(
    async () =>
      await olympiadService.getOlympiadParticipants({
        currentPage: currentPageQueryString,
        pageSize: pageSizeQueryString,
        dateFrom: createdDateFrom || undefined,
        dateTo: createdDateTo || undefined,
        region: region?.trim() || undefined,
        olympiadType: olympiadType?.value || undefined,
        search: searchQuery,
      }),
    [
      currentPageQueryString,
      pageSizeQueryString,
      createdDateFrom,
      createdDateTo,
      region,
      olympiadType,
      searchQuery,
    ]
  );

  const fetchAllApplicants = useCallback(async () => {
    const size = totalRecords > 0 ? totalRecords : 20;
    try {
      const response = await olympiadService.getOlympiadParticipants({
        currentPage: 1,
        pageSize: size,
      });
      setAllApplicants(response?.content || []);
      return response?.content || [];
    } catch (error) {
      console.error("Error fetching all applicants:", error);
      return [];
    }
  }, [totalRecords, setAllApplicants]);

  const handleOnLoadFetchedUsers = useCallback(
    (res) => {
      setApplicants(res?.content);
      setCurrentPage(res?.number + 1);
      setTotalPages(res?.totalPages);
      setTotalRecords(res?.totalElements);
      setPageSize(res?.size);
      const calculatedTotalPages = Math.ceil(res?.totalElements / res?.size);
      setTotalPages(calculatedTotalPages);
    },
    [
      setApplicants,
      setCurrentPage,
      setTotalPages,
      setTotalRecords,
      setPageSize,
    ]
  );

  const participantDataChecker = useCallback(
    () => (applicants?.length > 0 ? true : false),
    [applicants]
  );

  const onExportClick = async () => {
    const toExport =
      allApplicants?.length > 0 ? allApplicants : await fetchAllApplicants();
    if (!toExport || toExport.length === 0) {
      alert("No applicants available for export.");
      return;
    }

    const newWorkBook = XLSX.utils.book_new();
    const newWorkSheet = XLSX.utils.json_to_sheet(toExport);

    XLSX.utils.book_append_sheet(newWorkBook, newWorkSheet, "NewSheet1");
    XLSX.writeFile(newWorkBook, "NewFile.xlsx");
  };

  return (
    <MainPageHOC
      headerTitle={"Olympiad Participants"}
      pageHeader={
        <MainPageHeader
          totalRecords={totalRecords}
          pageName="Olympiad Participants"
          redirectTo={"add"}
          disableExportButton={true}
          showExportButton={true}
          onExportClick={onExportClick}
        />
      }
      filtersSection={
        <OlympiadPageFilters
          createdDateFrom={createdDateFrom}
          setCreatedDateFrom={handleCreatedDateFromChange}
          createdDateTo={createdDateTo}
          setCreatedDateTo={handleCreatedDateToChange}
          region={region}
          setRegion={handleRegionChange}
          olympiadType={olympiadType}
          setOlympiadType={handleOlympiadTypeChange}
          onClearAllFilters={handleClearAllFilters}
        />
      }
      tableHeader={
        <MainPageTableHeader
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchValue={search}
          onSearchChange={setSearch}
        />
      }
      tableBody={
        <FetcherComponent
          action={fetchUsers}
          onLoad={handleOnLoadFetchedUsers}
          key={[
            currentPageQueryString,
            pageSizeQueryString,
            createdDateFrom,
            createdDateTo,
            region,
            olympiadType?.value,
            searchQuery,
          ]}
        >
          <>
            <OlympiadPageTableBody
              users={applicants}
              setUsers={setApplicants}
            />
            <div className="card-inner">
              <PaginationComponent
                currentPageNumber={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                status={pageSizeQueryString}
              />
            </div>
          </>
        </FetcherComponent>
      }
      hasDataForRendering={participantDataChecker}
    />
  );
};
export default OlympiadPage;
